import React, { useState, useEffect, useRef } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  Tag, Printer, QrCode, FileText, Download, 
  Package, Search, CheckSquare, Square, BarChart3
} from 'lucide-react';
import QRCode from 'qrcode';

const LabelsTab = ({ plants, bins: propBins }) => {
  const [bins, setBins] = useState(propBins || []);
  const [articles, setArticles] = useState([]);
  const [stockEntries, setStockEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBins, setSelectedBins] = useState([]);
  const [labelPreview, setLabelPreview] = useState(null);
  const [reportType, setReportType] = useState('stock_value');
  const [selectedPlant, setSelectedPlant] = useState('');
  
  const printRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [binsRes, articlesRes, stockRes] = await Promise.all([
        api.get('/bins'),
        api.get('/articles'),
        api.get('/stock')
      ]);
      setBins(binsRes.data);
      setArticles(articlesRes.data);
      setStockEntries(stockRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBinSelection = (binId) => {
    setSelectedBins(prev => 
      prev.includes(binId) 
        ? prev.filter(id => id !== binId)
        : [...prev, binId]
    );
  };

  const selectAllBins = () => {
    if (selectedBins.length === filteredBins.length) {
      setSelectedBins([]);
    } else {
      setSelectedBins(filteredBins.map(b => b.id));
    }
  };

  const generateQRCode = async (text) => {
    try {
      return await QRCode.toDataURL(text, { width: 100, margin: 1 });
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  };

  const generateBinLabels = async () => {
    if (selectedBins.length === 0) {
      toast.error('Selectati cel putin o locatie BIN');
      return;
    }

    // Get bins in correct order: cabinet → shelf → position
    const sortedBins = selectedBins
      .map(id => bins.find(b => b.id === id))
      .filter(Boolean)
      .sort((a, b) => {
        const cabCmp = naturalSort(a.cabinet, b.cabinet);
        if (cabCmp !== 0) return cabCmp;
        const shelfCmp = naturalSort(a.shelf, b.shelf);
        if (shelfCmp !== 0) return shelfCmp;
        return naturalSort(a.position, b.position);
      });

    const labelsData = await Promise.all(
      sortedBins.map(async (bin) => {
        const qrData = await generateQRCode(`BIN:${bin.code}`);
        return { ...bin, qrCode: qrData };
      })
    );

    setLabelPreview(labelsData.filter(Boolean));
  };

  const printLabels = () => {
    if (!labelPreview || labelPreview.length === 0) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup blocat. Permiteti popup-uri pentru aceasta pagina.');
      return;
    }

    const labelsHtml = labelPreview.map(label => `
      <div style="
        border: 2px solid #000;
        padding: 12px;
        margin: 8px;
        width: 200px;
        display: inline-block;
        text-align: center;
        font-family: Arial, sans-serif;
        page-break-inside: avoid;
      ">
        <img src="${label.qrCode}" style="width: 80px; height: 80px;" />
        <div style="font-size: 16px; font-weight: bold; margin-top: 8px;">${label.code}</div>
        <div style="font-size: 12px; color: #666; margin-top: 4px;">${label.description || 'N/A'}</div>
      </div>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etichete BIN</title>
        <style>
          @media print {
            body { margin: 0; }
            @page { margin: 0.5cm; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }
        </style>
      </head>
      <body>
        ${labelsHtml}
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const generateStockReport = async () => {
    try {
      // Calculate stock value report
      const stockWithValue = [];
      let totalValue = 0;
      
      for (const entry of stockEntries) {
        const article = articles.find(a => a.id === entry.article_id);
        const bin = bins.find(b => b.id === entry.bin_id);
        
        if (article && entry.quantity > 0) {
          const unitPrice = article.unit_price || 0;
          const value = entry.quantity * unitPrice;
          totalValue += value;
          
          stockWithValue.push({
            article_name: article.name,
            article_code: article.code,
            bin_code: bin?.code || 'N/A',
            quantity: entry.quantity,
            unit: article.unit || 'buc',
            unit_price: unitPrice,
            total_value: value
          });
        }
      }

      // Sort by value descending
      stockWithValue.sort((a, b) => b.total_value - a.total_value);

      // Generate CSV
      const headers = ['Articol', 'Cod', 'Locatie BIN', 'Cantitate', 'UM', 'Pret unitar', 'Valoare totala'];
      const rows = stockWithValue.map(item => [
        item.article_name,
        item.article_code,
        item.bin_code,
        item.quantity,
        item.unit,
        item.unit_price.toFixed(2),
        item.total_value.toFixed(2)
      ]);
      rows.push(['', '', '', '', '', 'TOTAL:', totalValue.toFixed(2)]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `raport_valoare_stoc_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      toast.success(`Raport generat! Valoare totala: ${totalValue.toFixed(2)} RON`);
    } catch (error) {
      toast.error('Eroare la generarea raportului');
    }
  };

  const generateMovementsReport = async () => {
    try {
      const response = await api.get('/stock-movements', { params: { limit: 500 } });
      const movements = response.data;

      const headers = ['Data', 'Tip', 'Articol', 'Din BIN', 'In BIN', 'Cantitate', 'Referinta'];
      const rows = movements.map(m => [
        new Date(m.created_at).toLocaleString('ro-RO'),
        m.movement_type,
        m.article_name || 'N/A',
        m.from_bin_id ? bins.find(b => b.id === m.from_bin_id)?.code || m.from_bin_id : '-',
        m.to_bin_id ? bins.find(b => b.id === m.to_bin_id)?.code || m.to_bin_id : '-',
        m.quantity,
        m.reference_type ? `${m.reference_type}: ${m.reference_id}` : '-'
      ]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `raport_miscari_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      toast.success('Raport miscari generat!');
    } catch (error) {
      toast.error('Eroare la generarea raportului');
    }
  };

  // Natural sort helper for positions (P1 < P2 < P10)
  const naturalSort = (a, b) => {
    return (a || '').localeCompare(b || '', 'ro', { numeric: true, sensitivity: 'base' });
  };

  const filteredBins = bins.filter(bin =>
    bin.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bin.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    // Sort: cabinet → shelf → position (natural order)
    const cabCmp = naturalSort(a.cabinet, b.cabinet);
    if (cabCmp !== 0) return cabCmp;
    const shelfCmp = naturalSort(a.shelf, b.shelf);
    if (shelfCmp !== 0) return shelfCmp;
    return naturalSort(a.position, b.position);
  });

  return (
    <div className="space-y-6">
      {/* Labels Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Etichete BIN
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Select All */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cauta locatie BIN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={selectAllBins}>
              {selectedBins.length === filteredBins.length && filteredBins.length > 0 ? (
                <CheckSquare className="w-4 h-4 mr-2" />
              ) : (
                <Square className="w-4 h-4 mr-2" />
              )}
              {selectedBins.length === filteredBins.length && filteredBins.length > 0 ? 'Deselecteaza toate' : 'Selecteaza toate'}
            </Button>
          </div>

          {/* BIN Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[300px] overflow-y-auto">
            {filteredBins.map((bin) => (
              <div
                key={bin.id}
                onClick={() => toggleBinSelection(bin.id)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedBins.includes(bin.id)
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Checkbox checked={selectedBins.includes(bin.id)} />
                  <div>
                    <p className="font-mono text-sm font-semibold">{bin.code}</p>
                    <p className="text-xs text-slate-500 truncate">{bin.description || 'N/A'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t">
            <Badge variant="outline">{selectedBins.length} selectate</Badge>
            <Button 
              onClick={generateBinLabels} 
              disabled={selectedBins.length === 0}
              variant="outline"
            >
              <QrCode className="w-4 h-4 mr-2" />
              Genereaza Previzualizare
            </Button>
            <Button 
              onClick={printLabels} 
              disabled={!labelPreview || labelPreview.length === 0}
              className="btn-primary"
            >
              <Printer className="w-4 h-4 mr-2" />
              Printeaza Etichete
            </Button>
          </div>

          {/* Preview */}
          {labelPreview && labelPreview.length > 0 && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-3">Previzualizare etichete:</p>
              <div className="flex flex-wrap gap-4" ref={printRef}>
                {labelPreview.map((label) => (
                  <div 
                    key={label.id}
                    className="border-2 border-slate-300 p-3 bg-white rounded text-center"
                    style={{ width: '150px' }}
                  >
                    {label.qrCode && (
                      <img src={label.qrCode} alt="QR" className="mx-auto w-16 h-16" />
                    )}
                    <p className="font-mono font-bold text-sm mt-2">{label.code}</p>
                    <p className="text-xs text-slate-500 truncate">{label.description || 'N/A'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reports Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Rapoarte Stoc
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Stock Value Report */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Package className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Valoare Stoc</p>
                      <p className="text-sm text-slate-500">Raport cu valoarea totala per articol</p>
                    </div>
                  </div>
                  <Button onClick={generateStockReport} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Movements Report */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Miscari Stoc</p>
                      <p className="text-sm text-slate-500">Istoric intrari, iesiri, transferuri</p>
                    </div>
                  </div>
                  <Button onClick={generateMovementsReport} variant="outline">
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{articles.length}</p>
              <p className="text-sm text-slate-500">Articole</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{bins.length}</p>
              <p className="text-sm text-slate-500">Locatii BIN</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{stockEntries.length}</p>
              <p className="text-sm text-slate-500">Inregistrari stoc</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LabelsTab;
